import { el, btn } from '@/ui/dom';
import { cssColor } from '@/ui/format';
import { citySilhouette } from '@/ui/citySilhouettes';
import { CITIES } from '@/config/cities';
import { openSheet, type SheetHandle } from '@/ui/social/sheet';
import { panelHead, ridButton } from '@/ui/social/clubUi';
import {
  CREW_RACE_FIELD,
  CREW_TOUR_DEFAULT,
  CREW_TOUR_LENGTHS,
  type CrewRaceChoice,
  type CrewRaceInvite,
} from '@/social/crewLogic';

/**
 * clubRaceSheet.ts — CALL YOUR CLUB (M142, owner: "the person who starts the
 * club race should be able to pick a tour (with the tour lengths) or a quick
 * race, then the city for a quick race, then open or closed"). One sheet in
 * the Racer ID dashboard's language, read top to bottom in the owner's order:
 *
 *   RACE TYPE     QUICK RACE · CLUB TOUR
 *   CITY | STOPS  RANDOM + the nine cities (photo tiles) | 3 · 5 · 9
 *   WHO CAN JOIN  CLUB ONLY · OPEN
 *   START CLUB RACE / START CLUB TOUR (gold), with the choice summed up under
 *   it ("TOKYO · CLUB ONLY", "5 STOPS · OPEN").
 *
 * Every choice row is a real radio group (role="radiogroup", arrow keys move
 * and pick, one tab stop per row). The picks are remembered for the session,
 * so a second club race starts with one tap. The START footer is sticky, so
 * on a landscape phone (where the sheet may scroll) it is always on screen.
 *
 * House rules: players read "club" only; an open race's empty seats fill with
 * "racers" (online copy never names AI); no em dashes; gold = affirmative.
 * The copy helpers are pure and tested (tests/social/clubRaceSheet.test.ts).
 */

// ---- pure copy (tested) ---------------------------------------------------------

/** 'TOKYO', 'RIO DE JANEIRO', or 'RANDOM CITY' for an unpicked city. */
export function clubRaceCityLabel(city: string): string {
  const c = CITIES.find((x) => x.id === city);
  return c ? c.name.toUpperCase() : 'RANDOM CITY';
}

/** The START button's second line: 'TOKYO · CLUB ONLY', 'RANDOM CITY ·
 *  OPEN', '5 STOPS · OPEN'. */
export function clubRaceSummary(c: CrewRaceChoice): string {
  const what = c.tour > 0 ? `${c.tour} STOPS` : clubRaceCityLabel(c.city);
  return `${what} · ${c.visibility === 'public' ? 'OPEN' : 'CLUB ONLY'}`;
}

export function clubRaceStartLabel(c: CrewRaceChoice): string {
  return c.tour > 0 ? 'START CLUB TOUR' : 'START CLUB RACE';
}

/** The OPEN card's line. Owner rule: the backfill is "racers", never AI. */
export function clubRaceOpenDesc(tour: boolean): string {
  return `Empty seats fill with racers looking for a ${tour ? 'tour' : 'race'}.`;
}

export const CLUB_RACE_PRIVATE_DESC = 'Just your club. Start when everyone is ready.';
export const CLUB_RACE_TOUR_LINE = 'Random cities, a new route every tour.';
export const CLUB_RACE_FINE = `${CREW_RACE_FIELD} racers · invites go to your whole club`;

/** The race lobby's info line for a club race: '5-stop tour · Club only ·
 *  6 racers', 'Tokyo · Open race · 6 racers'. */
export function clubLobbyInfo(
  r: Pick<CrewRaceInvite, 'tourLen' | 'city' | 'visibility' | 'field'>,
): string {
  const open = r.visibility === 'public';
  if (r.tourLen > 0) {
    return `${r.tourLen}-stop tour · ${open ? 'Open' : 'Club only'} · ${r.field} racers`;
  }
  const c = CITIES.find((x) => x.id === r.city);
  return `${c ? c.name : r.city} · ${open ? 'Open race' : 'Club only'} · ${r.field} racers`;
}

// ---- the remembered pick (this session) ---------------------------------------------

export type ClubRaceType = 'quick' | 'tour';
type Visibility = 'private' | 'public';

export interface RacePick {
  type: ClubRaceType;
  /** A city id or 'random'. */
  city: string;
  stops: number;
  visibility: Visibility;
}

/** The last choice made on this sheet (module state: one session's memory,
 *  never persisted). Quick race, random city, club only to start with. */
let remembered: RacePick = {
  type: 'quick',
  city: 'random',
  stops: CREW_TOUR_DEFAULT,
  visibility: 'private',
};

/** What a pick asks Flow to start (a tour carries no city; Flow rolls one). */
export function clubRaceChoiceOf(p: RacePick): CrewRaceChoice {
  return {
    visibility: p.visibility,
    tour: p.type === 'tour' ? p.stops : 0,
    city: p.type === 'quick' ? p.city : 'random',
  };
}

// ---- icons ----------------------------------------------------------------------------

const FLAG_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M5 2.8h1.6V22H5zM7.4 3.4h12.4l-2.6 4.4 2.6 4.4H7.4z" opacity=".95"/>' +
  '<path d="M9 4.6h2.4V7H9zm4.8 0h2.4V7h-2.4zM11.4 7h2.4v2.4h-2.4zM9 9.4h2.4v2.4H9zm4.8 0h2.4v2.4h-2.4z" fill="#000" opacity=".45"/></svg>';
const BOLT_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.6 2 4.6 13.6h6.1L9.4 22l9.2-12h-6.2z"/></svg>';
const ROUTE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M6 13.2a3.4 3.4 0 0 1 3.4 3.4C9.4 19 6 22 6 22s-3.4-3-3.4-5.4A3.4 3.4 0 0 1 6 13.2Zm0 2.1a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"/>' +
  '<path d="M18 2a3.4 3.4 0 0 1 3.4 3.4C21.4 7.8 18 10.8 18 10.8s-3.4-3-3.4-5.4A3.4 3.4 0 0 1 18 2Zm0 2.1a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"/>' +
  '<path d="M8.6 19.6h5.2a3.3 3.3 0 0 0 0-6.6h-3.6a3.3 3.3 0 0 1 0-6.6h3.2" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-dasharray="1.8 2.4" opacity=".75"/></svg>';
const LOCK_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1Zm2 0h6V8a3 3 0 0 0-6 0v2Z"/></svg>';
const GLOBE_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9"/><ellipse cx="12" cy="12" rx="4.2" ry="9"/><path d="M3.6 8.6h16.8M3.6 15.4h16.8"/></svg>';
const DICE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm2.5 3a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3ZM7.5 15a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Zm9 0a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3Z"/></svg>';
const CHECK_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5.5 12.5 4 4 9-9" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// ---- radio groups -----------------------------------------------------------------------

interface RadioItem<T> {
  value: T;
  /** Fills the option's button (its visible text is its accessible name). */
  build: (b: HTMLButtonElement) => void;
}

interface RadioGroup<T> {
  el: HTMLDivElement;
  sync(value: T): void;
  setDisabled(disabled: boolean): void;
  focusChecked(): void;
}

let headSeq = 0;

/** A section: the dashboard's ── TITLE ── head, labelling what follows. */
function section(
  parent: HTMLElement,
  className: string,
  title: string,
): { sec: HTMLDivElement; titleEl: HTMLElement; id: string } {
  const sec = el('div', `crs-sec ${className}`, parent);
  const head = panelHead(sec, title, 'crs-head');
  const titleEl = (head.querySelector('.club-head-title') as HTMLElement | null) ?? head;
  const id = `crs-head-${++headSeq}`;
  titleEl.id = id;
  return { sec, titleEl, id };
}

/**
 * One row of choices with radio semantics: role="radiogroup", each option a
 * role="radio" button with aria-checked, ONE tab stop (the checked option),
 * and the arrow keys / Home / End move AND pick (the WAI-ARIA radio pattern).
 * Handled keys stop here, so the menus' global key handlers never see them.
 */
function radioGroup<T>(
  parent: HTMLElement,
  className: string,
  labelledBy: string,
  items: RadioItem<T>[],
  onPick: (v: T) => void,
): RadioGroup<T> {
  const g = el('div', `crs-group ${className}`, parent);
  g.setAttribute('role', 'radiogroup');
  g.setAttribute('aria-labelledby', labelledBy);
  const buttons = items.map((it) => {
    const b = btn('', 'crs-opt', g, () => onPick(it.value));
    b.setAttribute('role', 'radio');
    b.setAttribute('aria-checked', 'false');
    it.build(b);
    return b;
  });
  let checked = 0;
  g.addEventListener('keydown', (e) => {
    const n = buttons.length;
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const from = at >= 0 ? at : checked;
    let to: number;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        to = (from + 1) % n;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        to = (from - 1 + n) % n;
        break;
      case 'Home':
        to = 0;
        break;
      case 'End':
        to = n - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    e.stopPropagation();
    const next = buttons[to];
    if (!next || next.disabled) return;
    next.focus();
    next.click();
  });
  return {
    el: g,
    sync(value: T): void {
      items.forEach((it, i) => {
        const on = it.value === value;
        const b = buttons[i];
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', String(on));
        b.tabIndex = on ? 0 : -1;
        if (on) checked = i;
      });
    },
    setDisabled(disabled: boolean): void {
      for (const b of buttons) b.disabled = disabled;
    },
    focusChecked(): void {
      buttons[checked]?.focus();
    },
  };
}

/** A choice card: icon tile, title, a small line, the radio dot. */
function cardBuilder(
  icon: string,
  title: string,
  line: string,
  extra = '',
): { build: (b: HTMLButtonElement) => void; lineEl: () => HTMLElement | null } {
  let lineEl: HTMLElement | null = null;
  return {
    build: (b) => {
      b.classList.add('crs-card');
      if (extra) b.classList.add(extra);
      el('span', 'crs-card-ic', b).innerHTML = icon;
      const t = el('span', 'crs-card-text', b);
      el('span', 'crs-card-title', t).textContent = title;
      lineEl = el('span', 'crs-card-line', t);
      lineEl.textContent = line;
      el('span', 'crs-dot', b);
    },
    lineEl: () => lineEl,
  };
}

// ---- the sheet ------------------------------------------------------------------------

export interface ClubRaceSheetOptions {
  crewName: string;
  /** Start it; resolve to an error line for the sheet, or null (it closes). */
  onStart: (choice: CrewRaceChoice) => Promise<string | null>;
  reducedMotion?: boolean;
}

export function openClubRaceSheet(o: ClubRaceSheetOptions): SheetHandle {
  // Restore the session's last pick (a city that no longer exists → random).
  const pick: RacePick = { ...remembered };
  if (pick.city !== 'random' && !CITIES.some((c) => c.id === pick.city)) pick.city = 'random';
  if (!CREW_TOUR_LENGTHS.includes(pick.stops)) pick.stops = CREW_TOUR_DEFAULT;

  const sheet = openSheet({
    kicker: o.crewName.toUpperCase(),
    title: 'CALL YOUR CLUB',
    className: 'crew-sheet-race crs-sheet',
    reducedMotion: o.reducedMotion,
  });
  const b = sheet.body;
  b.classList.add('crs-body');

  // 1. RACE TYPE
  const type = section(b, 'crs-sec-type', 'RACE TYPE');
  const typeGroup = radioGroup<ClubRaceType>(
    type.sec,
    'crs-cards',
    type.id,
    [
      { value: 'quick', build: cardBuilder(BOLT_SVG, 'QUICK RACE', 'ONE CITY · ONE RACE').build },
      { value: 'tour', build: cardBuilder(ROUTE_SVG, 'CLUB TOUR', 'CITY TO CITY · POINTS').build },
    ],
    (v) => {
      pick.type = v;
      refresh();
    },
  );

  // 2. CITY (quick race) or STOPS (tour) — one section, its head swaps.
  const place = section(b, 'crs-sec-place', 'CITY');
  const cityGroup = radioGroup<string>(
    place.sec,
    'crs-cities',
    place.id,
    [
      {
        value: 'random',
        build: (t) => {
          t.classList.add('crs-city', 'crs-city-random');
          const art = el('span', 'crs-city-art', t);
          el('span', 'crs-random-ic', art).innerHTML = DICE_SVG;
          el('span', 'crs-city-name', t).textContent = 'RANDOM';
          el('span', 'crs-check', t).innerHTML = CHECK_SVG;
        },
      },
      ...CITIES.map((city) => ({
        value: city.id,
        build: (t: HTMLButtonElement) => {
          t.classList.add('crs-city');
          t.style.setProperty('--city-accent', cssColor(city.theme.roadLine));
          const art = el('span', 'crs-city-art', t);
          // The skyline is the fallback art; the photo covers it when it loads.
          el('span', 'crs-city-sil', art).innerHTML = citySilhouette(city.id);
          const photo = document.createElement('img');
          photo.className = 'crs-city-photo';
          photo.alt = '';
          photo.decoding = 'async';
          photo.draggable = false;
          photo.addEventListener('error', () => photo.remove());
          photo.src = `${import.meta.env.BASE_URL}cities/${city.id}.webp`;
          art.appendChild(photo);
          el('span', 'crs-city-name', t).textContent = city.name.toUpperCase();
          el('span', 'crs-check', t).innerHTML = CHECK_SVG;
        },
      })),
    ],
    (v) => {
      pick.city = v;
      refresh();
    },
  );
  const stopsWrap = el('div', 'crs-stops-wrap', place.sec);
  const stopsGroup = radioGroup<number>(
    stopsWrap,
    'crs-stops',
    place.id,
    CREW_TOUR_LENGTHS.map((n) => ({
      value: n,
      build: (t: HTMLButtonElement) => {
        t.classList.add('crs-stop');
        el('span', 'crs-stop-n', t).textContent = String(n);
        el('span', 'crs-stop-l', t).textContent = 'STOPS';
      },
    })),
    (v) => {
      pick.stops = v;
      refresh();
    },
  );
  el('div', 'crs-tour-line', stopsWrap).textContent = CLUB_RACE_TOUR_LINE;

  // 3. WHO CAN JOIN
  const who = section(b, 'crs-sec-who', 'WHO CAN JOIN');
  const openCard = cardBuilder(GLOBE_SVG, 'OPEN', clubRaceOpenDesc(false), 'crs-card-open');
  const whoGroup = radioGroup<Visibility>(
    who.sec,
    'crs-cards crs-whos',
    who.id,
    [
      { value: 'private', build: cardBuilder(LOCK_SVG, 'CLUB ONLY', CLUB_RACE_PRIVATE_DESC).build },
      { value: 'public', build: openCard.build },
    ],
    (v) => {
      pick.visibility = v;
      refresh();
    },
  );

  // 4. START (a sticky footer: always reachable when the sheet scrolls).
  const foot = el('div', 'crs-foot', b);
  const err = el('div', 'crew-sheet-error crs-error', foot);
  err.setAttribute('role', 'alert');
  const actions = el('div', 'crew-sheet-actions crs-actions', foot);
  const first = clubRaceChoiceOf(pick);
  let busy = false;
  const start = ridButton(
    actions,
    {
      variant: 'gold',
      icon: FLAG_SVG,
      label: clubRaceStartLabel(first),
      sub: clubRaceSummary(first),
      className: 'crs-start',
    },
    () => {
      if (busy) return;
      const choice = clubRaceChoiceOf(pick);
      setBusy(true, choice);
      err.textContent = '';
      const fail = (line: string): void => {
        err.textContent = line;
        setBusy(false, choice);
      };
      o.onStart(choice).then(
        (e) => {
          if (e) fail(e);
          else sheet.close();
        },
        () => fail('Could not start the club race. Check your connection.'),
      );
    },
  );
  el('div', 'crew-fine crs-fine', foot).textContent = CLUB_RACE_FINE;

  const groups = [typeGroup, cityGroup, stopsGroup, whoGroup];
  function setBusy(on: boolean, choice: CrewRaceChoice): void {
    busy = on;
    sheet.panel.classList.toggle('crs-busy', on);
    for (const g of groups) g.setDisabled(on);
    start.el.disabled = on;
    start.setLabel(on ? 'STARTING…' : clubRaceStartLabel(choice), clubRaceSummary(choice));
  }

  function refresh(): void {
    const tour = pick.type === 'tour';
    typeGroup.sync(pick.type);
    cityGroup.sync(pick.city);
    stopsGroup.sync(pick.stops);
    whoGroup.sync(pick.visibility);
    cityGroup.el.style.display = tour ? 'none' : '';
    stopsWrap.style.display = tour ? '' : 'none';
    place.titleEl.textContent = tour ? 'STOPS' : 'CITY';
    const line = openCard.lineEl();
    if (line) line.textContent = clubRaceOpenDesc(tour);
    sheet.panel.classList.toggle('crs-tour', tour);
    const choice = clubRaceChoiceOf(pick);
    if (!busy) start.setLabel(clubRaceStartLabel(choice), clubRaceSummary(choice));
    remembered = { ...pick };
  }

  refresh();
  typeGroup.focusChecked();
  return sheet;
}
